import React, { useEffect } from 'react';
import { Box, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import classNames from 'classnames';

const useStyles = makeStyles((theme: Theme) => ({
  embeddableViewer: {
    backgroundColor: theme.palette.background.paper,
  },
  hasBorder: {
    boxShadow: 'inset 0px 0px 0px 1px rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(2.5),
  },
  iframe: {
    border: 0,
    height: '100%',
    left: 0,
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  iframeContainer: {
    height: 0,
    paddingBottom: '62.5%',
    position: 'relative',
    width: '100%',
  },
  rounded: {
    borderRadius: theme.spacing(1),
  },
}));

export interface EmbeddableViewerProps {
  className?: string;
  hasBorder?: boolean;
  name: string;
  onView?: () => void;
  rounded?: boolean;
  tabIndex?: number;
  url: string;
}

const EmbeddableViewer = React.forwardRef<
  HTMLDivElement,
  EmbeddableViewerProps
>((props: EmbeddableViewerProps, ref): JSX.Element => {
  const classes = useStyles();

  const {
    className,
    hasBorder = false,
    name,
    onView,
    rounded = false,
    tabIndex,
    url,
  } = props;

  /**
   * Not using onView as a dependency because it would depend on the parents
   * to add that callback in a `useCallback` hook, to make sure the reference
   * isn't overwritten in each parent re-render, leading the embeddable
   * content to be track the content viewed event N times (for every parent re-render)
   */
  useEffect(() => {
    if (onView) {
      onView();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      {...{ ref }}
      className={classNames(className, classes.embeddableViewer, {
        [classes.hasBorder]: hasBorder,
        [classes.rounded]: rounded,
      })}
    >
      <Box className={classes.iframeContainer}>
        <iframe
          allow="autoplay; fullscreen; picture-in-picture; camera; microphone; display-capture"
          allowFullScreen
          className={classes.iframe}
          referrerPolicy="no-referrer-when-downgrade"
          sandbox="allow-scripts allow-popups allow-top-navigation-by-user-activation allow-forms allow-same-origin"
          src={url}
          tabIndex={tabIndex}
          title={name}
        />
      </Box>
    </Box>
  );
});

export default EmbeddableViewer;
